'use client';

import { useAuthStore } from '../../stores';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  Lock,
  AlertTriangle,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';

// Neusym Gate API Configuration
const NEUSYM_API_URL =
  import.meta.env.VITE_NEUSYM_API_URL || 'https://api.neusym.com';

/**
 * GateProtectedRoute - Wraps content that requires Neusym Gate access
 * @param {Object} props
 * @param {string} props.gateSlug - The slug of the gate to check access against
 * @param {string} props.gateId - Alternative: The OID of the gate
 * @param {React.ReactNode} props.children - Content to render if access is granted
 * @param {React.ReactNode} props.fallback - Optional custom fallback for denied access
 */
export function GateProtectedRoute({ gateSlug, gateId, children, fallback }) {
  const { walletAddress } = useAuthStore();
  const address = walletAddress;

  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['gate-access', gateSlug || gateId, address],
    queryFn: async () => {
      if (!address) return null;

      let apiUrl;
      if (gateSlug) {
        apiUrl = `${NEUSYM_API_URL}/api/gates/slug/${gateSlug}/check/${address}`;
      } else if (gateId) {
        apiUrl = `${NEUSYM_API_URL}/api/gates/${gateId}/check/${address}`;
      } else {
        throw new Error('Either gateSlug or gateId must be provided');
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Gate access check failed');
      }
      return result;
    },
    enabled: !!address && (!!gateSlug || !!gateId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

  // Determine access status based on query result
  const accessStatus = {
    isLoading: isLoading && !!address,
    hasAccess: data?.hasAccess || false,
    error: !address
      ? 'wallet_not_connected'
      : queryError
      ? 'network_error'
      : data?.hasAccess === false
      ? 'access_denied'
      : null,
    gateName: data?.gate?.name || 'Protected Gate',
  };

  // Loading state
  if (accessStatus.isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="relative">
            <div className="h-16 w-16 mx-auto rounded-full bg-linear-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-white font-medium">Verifying Gate Access</p>
            <p className="text-gray-500 text-sm">
              Checking your wallet authorization...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Access granted - render children
  if (accessStatus.hasAccess) {
    return children;
  }

  // Custom fallback provided
  if (fallback) {
    return fallback;
  }

  // Default access denied UI
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="bg-black/50 backdrop-blur-xl border border-white/10 rounded-lg p-8 text-center space-y-6">
          {/* Icon */}
          <div className="relative mx-auto w-fit">
            <div className="h-24 w-24 rounded-full bg-linear-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center animate-pulse">
              {accessStatus.error === 'wallet_not_connected' ? (
                <Lock className="h-12 w-12 text-orange-400" />
              ) : (
                <Shield className="h-12 w-12 text-red-400" />
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-red-500 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-white" />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-3">
            <h2 className="text-3xl font-light tracking-wider text-white uppercase">
              {accessStatus.error === 'wallet_not_connected'
                ? 'Wallet Required'
                : '🚫 Access Denied'}
            </h2>
            <p className="text-gray-400 text-base">
              {accessStatus.error === 'wallet_not_connected'
                ? 'Please connect your wallet to access this gated content.'
                : accessStatus.error === 'network_error'
                ? 'Unable to verify gate access. Please check your connection and try again.'
                : `Sorry, you don't have permission to view this content.`}
            </p>
          </div>

          {/* Access Denied Explanation */}
          {accessStatus.error !== 'wallet_not_connected' && (
            <div className="bg-linear-to-br from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-lg p-4 text-left space-y-3">
              <p className="text-red-400 text-sm font-medium">
                Why am I seeing this?
              </p>
              <ul className="text-gray-400 text-sm space-y-2 list-disc list-inside">
                <li>
                  Your wallet address is{' '}
                  <span className="text-red-400">not on the whitelist</span> for
                  this gate
                </li>
                <li>
                  Only pre-approved wallets can access this protected content
                </li>
                <li>
                  The gate owner controls who can be added to the whitelist
                </li>
              </ul>
            </div>
          )}

          {/* Protected Content Info */}
          <div className="bg-linear-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-lg p-4 text-left">
            <p className="text-cyan-400 text-xs uppercase tracking-widest mb-2">
              🔒 This page is protected by
            </p>
            <p className="text-white font-medium text-lg">
              Neusym Gate Access Control
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Neusym Gates use decentralized access lists to control who can
              view protected content. Only wallets that have been explicitly
              added by the gate owner can pass through.
            </p>
          </div>

          {/* Wallet Address (if connected but denied) */}
          {walletAddress &&
            accessStatus.error !== 'wallet_not_connected' && (
              <div className="bg-white/5 border border-white/10 rounded px-4 py-3">
                <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">
                  Your Wallet Address
                </p>
                <code className="text-xs text-gray-300 font-mono break-all">
                  {walletAddress}
                </code>
                <p className="text-[10px] text-red-400 mt-2">
                  This address is not authorized
                </p>
              </div>
            )}

          {/* Gate Info */}
          {accessStatus.gateName && (
            <div className="bg-white/5 border border-white/10 rounded px-4 py-3">
              <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">
                Gate Name
              </p>
              <p className="text-cyan-400 font-medium">
                {accessStatus.gateName}
              </p>
            </div>
          )}

          {/* What can I do? */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-left">
            <p className="text-white text-sm font-medium mb-2">
              What can I do?
            </p>
            <ul className="text-gray-400 text-sm space-y-1">
              <li>• Contact the gate owner to request whitelist access</li>
              <li>• Visit Neusym to learn more about gates</li>
              <li>• Make sure you're connected with the correct wallet</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-2">
            <a
              href="https://neusym.io/gates"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
                Request Access on Neusym
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/">
              <Button
                variant="outline"
                className="w-full border-white/20 text-gray-300 hover:bg-white/5 hover:border-white/30"
              >
                Return Home
              </Button>
            </Link>
            <a
              href="https://neusym.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-500 hover:text-cyan-400 transition-colors flex items-center justify-center gap-1 mt-2"
            >
              Learn more about Neusym Gate
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {/* Footer */}
          <p className="text-gray-600 text-xs pt-4 border-t border-white/5">
            Powered by <span className="text-cyan-400">Neusym</span> •
            Decentralized Access Control
          </p>
        </div>
      </div>
    </div>
  );
}

export default GateProtectedRoute;
